import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Download, ExternalLink, Maximize2, Book, CheckCircle2, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { celebrate } from "@/lib/celebrate";

function Recommendations({ books_read_count }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const generate = async () => {
    setLoading(true);
    setOpen(true);
    try {
      const r = await api.post("/recommendations/me");
      setData(r.data);
    } catch {
      toast.error("No se pudieron generar las recomendaciones");
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={generate}
        data-testid="generate-recos-button"
        className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-gradient-to-r from-[#1B2E7A] to-[#4A7C59] text-white text-sm font-medium hover:shadow-lg transition-all"
      >
        <Sparkles className="h-4 w-4" />
        Recomendar cursos relacionados con IA
      </button>
    );
  }

  return (
    <div className="mt-6 bg-gradient-to-br from-[#F3F4F6] to-[#FFFFFF] border border-[#E5E7EB] rounded-md p-6" data-testid="recos-panel">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-4 w-4 text-[#1B2E7A]" />
        <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">Recomendaciones IA</p>
      </div>
      {loading ? (
        <p className="text-sm text-[#374151]">Analizando tus lecturas...</p>
      ) : !data ? null : data.recommendations?.length === 0 ? (
        <div className="prose prose-sm max-w-none whitespace-pre-wrap text-[#1A1A1A]">
          {data.reasoning_markdown}
        </div>
      ) : (
        <div>
          <div className="prose prose-sm max-w-none whitespace-pre-wrap text-[#1A1A1A] mb-4">
            {data.reasoning_markdown}
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {data.recommendations.map((r) => (
              <Link
                key={r.course_id}
                to={`/courses/${r.course_id}`}
                data-testid={`reco-${r.course_id}`}
                className="group bg-white border border-[#E5E7EB] hover:border-[#E5E7EB] hover:shadow-md rounded-md p-4 transition-all flex flex-col"
              >
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">{r.course_topic}</p>
                <h4 className="font-heading text-base text-[#1A1A1A] mt-1 mb-2 leading-tight">
                  {r.course_title}
                </h4>
                <p className="text-xs text-[#374151] flex-1 leading-relaxed">{r.reason}</p>
                <span className="text-xs text-[#1B2E7A] font-medium mt-2 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                  Ir al curso →
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function BookReader() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [marking, setMarking] = useState(false);

  const fetchBook = async () => {
    try {
      const r = await api.get(`/library/${id}`);
      setBook(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBook();
    // eslint-disable-next-line
  }, [id]);

  const toggleRead = async () => {
    if (!book) return;
    setMarking(true);
    try {
      if (book.is_read) {
        await api.delete(`/library/${id}/mark-read`);
        toast.success("Marcado como pendiente");
      } else {
        const { data } = await api.post(`/library/${id}/mark-read`);
        toast.success("¡Libro completado!");
        celebrate("book_completed");
        if (data?.points_awarded) {
          toast.success(`+${data.points_awarded.points} pts · ${data.points_awarded.reason}`, { duration: 4500 });
        }
      }
      await fetchBook();
    } catch {
      toast.error("Error al actualizar el estado");
    } finally {
      setMarking(false);
    }
  };

  if (loading) return <div className="text-[#374151]">Cargando libro...</div>;
  if (!book) return <div className="text-[#374151]">Libro no encontrado.</div>;

  const src = book.pdf_type === "url" ? book.pdf_url : `${API}/files/${book.pdf_url}`;
  const accent = book.color_accent || "#1B2E7A";

  return (
    <div data-testid="book-reader-page">
      <button
        onClick={() => navigate("/biblioteca")}
        className="inline-flex items-center gap-1 text-sm text-[#374151] hover:text-[#1A1A1A] mb-6"
        data-testid="back-to-library"
      >
        <ChevronLeft className="h-4 w-4" /> Biblioteca
      </button>

      {/* Header card */}
      <div
        className="rounded-md overflow-hidden border border-[#E5E7EB] mb-6 fade-up"
        style={{
          background: `linear-gradient(110deg, ${accent} 0%, ${accent}E0 60%, #FFFFFF 60%)`,
        }}
      >
        <div className="flex flex-col md:flex-row gap-6 p-6 md:p-8">
          <div className="md:w-1/3 flex md:items-center">
            <div
              className="w-full max-w-[220px] aspect-[3/4] rounded-md p-5 flex flex-col justify-between text-white relative overflow-hidden shadow-lg"
              style={{ background: `linear-gradient(155deg, ${accent} 0%, ${accent}CC 100%)` }}
            >
              <div className="absolute left-0 top-0 bottom-0 w-2 bg-black/20" />
              <div className="absolute left-2 top-0 bottom-0 w-px bg-white/30" />
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/70 relative z-10">Mueblería Canales</p>
              <Book className="h-7 w-7 text-white/80 relative z-10" strokeWidth={1.3} />
              <h3 className="font-heading text-base text-white leading-tight relative z-10">{book.title}</h3>
            </div>
          </div>
          <div className="flex-1 md:py-2">
            <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-2">{book.category}</p>
            <h1 className="font-heading text-3xl sm:text-4xl text-[#1A1A1A] mb-2">{book.title}</h1>
            {book.subtitle && (
              <p className="text-base text-[#374151] mb-3 italic">{book.subtitle}</p>
            )}
            <p className="text-sm text-[#374151] leading-relaxed max-w-2xl">{book.description}</p>
            <div className="flex items-center gap-3 mt-5 flex-wrap">
              <Button
                onClick={() => setFullscreen((v) => !v)}
                className="bg-[#1B2E7A] hover:bg-[#13205A] text-white"
                data-testid="toggle-fullscreen-button"
              >
                <Maximize2 className="h-4 w-4 mr-2" />
                {fullscreen ? "Salir de lectura inmersiva" : "Lectura inmersiva"}
              </Button>
              <Button
                onClick={toggleRead}
                disabled={marking}
                variant={book.is_read ? "outline" : "default"}
                className={
                  book.is_read
                    ? "border-[#1B2E7A] text-[#1B2E7A] hover:bg-[#F3F4F6] bg-transparent"
                    : "bg-[#DC2626] hover:bg-[#a85542] text-white"
                }
                data-testid="mark-read-button"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                {marking
                  ? "Guardando..."
                  : book.is_read
                  ? "Marcado como leído"
                  : "Marcar como leído"}
              </Button>
              <a
                href={src}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-[#E5E7EB] text-sm text-[#1B2E7A] hover:bg-[#F3F4F6] transition-colors"
                data-testid="open-external-button"
              >
                <ExternalLink className="h-4 w-4" /> Abrir en nueva pestaña
              </a>
              <a
                href={src}
                download
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-[#E5E7EB] text-sm text-[#374151] hover:bg-[#F3F4F6] transition-colors"
                data-testid="download-button"
              >
                <Download className="h-4 w-4" /> Descargar PDF
              </a>
              <span className="text-xs text-[#737373]">{book.estimated_pages} páginas</span>
            </div>
            {book.is_read && <Recommendations />}
          </div>
        </div>
      </div>

      {/* PDF viewer */}
      <div
        className={`bg-[#1A1A1A] border border-[#E5E7EB] rounded-md overflow-hidden ${
          fullscreen ? "fixed inset-4 z-50" : "relative"
        }`}
        data-testid="book-pdf-container"
      >
        {fullscreen && (
          <button
            onClick={() => setFullscreen(false)}
            className="absolute top-3 right-3 z-10 px-3 py-1.5 rounded-md bg-white/95 hover:bg-white text-sm text-[#1A1A1A] font-medium"
            data-testid="exit-fullscreen-button"
          >
            Cerrar
          </button>
        )}
        <iframe
          src={src}
          title={book.title}
          className="w-full"
          style={{ height: fullscreen ? "100%" : "78vh" }}
          data-testid="book-pdf-iframe"
        />
      </div>
    </div>
  );
}
